/* eslint-disable indent */
import { getLicenseStatus, getPermissions } from '@/api/services/user';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { SelectLayout } from '@/layouts/SelectLayout';
import { useResponsiveHeight } from '@/hooks/useResponsiveDimensions';
import { SquareGrid } from '@/components/SquareGrid';
import theme from '@/config/theme';
import { useStoreActions, useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { AntDesign } from '@expo/vector-icons';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { styles } from './SelectExercise.styles';
import { Text } from '@/components/Text';
import encryptedStorage from '@/stores/encryptedStorage';
import { setStorageKey } from '@/utils/setStorageKey';
import ExerciseCard from './ExerciseCard';
import { TASKS } from './constants';
import { useDialog } from '@/hooks/useDialog';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { ExerciseModalitySettings, Patient, Set } from '@/stores/store.types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { isPatientApp } from '@/config/constants';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

interface SelectExerciseProps {
  exerciseClass: string;
  patientId: string;
  setId: string;
  isNetworkError?: string;
  exerciseSettings?: Record<string, string | ExerciseModalitySettings>;
  isSelf: boolean;
  selectedPatient?: Patient;
  activeSets?: unknown;
  preview?: boolean;
}

export function SelectExercise(props: SelectExerciseProps) {
  const { navigate, ...navigation } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { showDialog } = useDialog();
  const { showNotification } = useNotification();

  const { isTherapistDemo, toolTipNumber, selfTrainingSelection } =
    useStoreState((state) => state);

  const { setToolTipNumber } = useStoreActions((actions) => ({
    setToolTipNumber: actions.setToolTipNumber,
  }));

  const [exerciseSettings, setExerciseSettings] = useState(
    props.exerciseSettings?.exerciseSettings,
  );
  const [activeExerciseSet, setActiveExerciseSet] = useState<{
    [key: string]: boolean;
  }>({ Sprechen: false, Lesen: false, Schreiben: false, Verstehen: false });

  const {
    patientId,
    setId,
    isSelf,
    selectedPatient,
    activeSets,
    preview,
    exerciseClass: setType, // setType = wordset | sentenceset | textset
  } = props;

  const isTextset = setType === 'textset';
  const tasksKey = isTextset ? setType : 'default';

  const toggleActiveLocalSetSettings = async (
    id: string,
    key: 'Lesen' | 'Schreiben' | 'Sprechen' | 'Verstehen',
  ) => {
    try {
      const { exerciseSettings } = (await encryptedStorage().getItem(
        setStorageKey(setId, setType),
      )) as Set;
      const prevState = exerciseSettings[key]?.active;
      const toChange = {
        ...exerciseSettings,
        [key]: {
          ...exerciseSettings[key],
          active: !prevState,
        },
      };
      setExerciseSettings(toChange);
      const newData = {
        ...props.exerciseSettings,
        exerciseSettings: toChange,
      };

      await encryptedStorage().setItem(setStorageKey(id, setType), newData);
    } catch (error) {
      logError('Could not activate local set', error);
    }
  };

  const responsiveFontHeight = useResponsiveHeight(2);

  const getResponsiveFontSize = (factor: number) => {
    return responsiveFontHeight * factor;
  };

  const responsizeIconSizeBig = getResponsiveFontSize(8);

  let responsiveStyles = StyleSheet.create({
    fontSize: {
      fontSize: getResponsiveFontSize(4),
    },
  });

  // Using the useMemo hook to cache the map result and let the exerciseTypes variable get re-initialized only of one of the deps changes
  const exerciseTypes = useMemo(() => {
    return TASKS[tasksKey].map((exercise) => {
      return (
        <ExerciseCard
          label={isSelf ? exercise.exerciseSettingsKey : exercise.label}
          iconName={exercise.iconName}
          isActive={activeExerciseSet?.[exercise.exerciseSettingsKey]}
          onPress={() => {
            setActiveExerciseSet((prev) => ({
              ...prev,
              [exercise.exerciseSettingsKey]:
                !prev?.[exercise.exerciseSettingsKey],
            }));
            if (!isSelf) {
              toggleActiveLocalSetSettings(setId, exercise.exerciseSettingsKey);
            }
          }}
        />
      );
    });
  }, [activeExerciseSet]);

  const onStartPress = async () => {
    const localWordset = (await encryptedStorage().getItem(
      setStorageKey(setId, setType),
    )) as Set | undefined;
    const hasInternet = await hasInternetAsync();

    if (!localWordset?.preparedSet.exercises && !hasInternet) {
      logError('No internet connection and no prepared exercises available');
      showNotification({
        title:
          'Keine Verbindung zum neolexon Server. Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
        type: 'error',
      });

      return;
    } else if (!localWordset?.preparedSet.exercises && hasInternet) {
      logError(
        'No prepared exercises found, but internet connection is available.',
      );
    }

    const params = {
      setId,
      patientId,
      setType,
      localWordset,
      isSelf,
      selectedPatient,
      activeSets,
      activatedExerciseModalities: activeExerciseSet,
      preview,
    };

    const obj = await getPermissions(false, !isPatientApp);

    if (obj?.permissions.plan !== 'gratis' || preview) {
      navigate(isTextset ? 'AufgabenExerciseText' : 'AufgabenExercise', params);
    } else {
      const licenseStatus = await getLicenseStatus();
      showDialog(PREPARED_DIALOGS.licenseExpiredDialog(licenseStatus));
    }
  };

  const buttonsRightSide = useMemo(() => {
    const disabled = Object.values(activeExerciseSet).every(
      (value) => value === false,
    );

    return [
      isSelf ? null : (
        <PressableNeolexon
          key="settings"
          style={[styles.darkGrey, responsiveStyles.fontSize]}
          onPress={async () => {
            const exerciseSettings: Set = (await encryptedStorage().getItem(
              setStorageKey(setId, setType),
            )) as Set;

            navigate('AufgabenSettings', {
              setType,
              setId,
              exerciseSettings,
            });
          }}
        >
          <FontAwesome
            name="gear"
            size={responsizeIconSizeBig}
            color={theme.colors.darkGrey}
          />
        </PressableNeolexon>
      ),
      <Pressable
        style={[styles.startButtonFull, disabled && styles.inActiveStyle]}
        key="start"
        onPress={onStartPress}
        disabled={disabled}
      >
        <View style={styles.centerAlignment}>
          <AntDesign
            name="caret-right"
            size={responsizeIconSizeBig}
            color={theme.colors.primary}
          />
        </View>
        <Text
          testID="start-exercise"
          style={[styles.startText, responsiveStyles.fontSize]}
        >
          Start
        </Text>
      </Pressable>,
    ];
  }, [
    responsiveStyles,
    responsizeIconSizeBig,
    isSelf ? activeExerciseSet : JSON.stringify(exerciseSettings),
  ]);

  useEffect(() => {
    function beforeRemove() {
      if (isTherapistDemo && toolTipNumber === 4) {
        setToolTipNumber(3);
      }
    }
    navigation.addListener('beforeRemove', beforeRemove);
    return () => {
      navigation.removeListener('beforeRemove', beforeRemove);
    };
  }, []);

  useEffect(() => {
    TASKS[tasksKey].map((exercise) => {
      setActiveExerciseSet((prev) => ({
        ...prev,
        [exercise.exerciseSettingsKey]: isSelf
          ? selfTrainingSelection[exercise.exerciseSettingsKey]
          : exerciseSettings[exercise.exerciseSettingsKey]?.active,
      }));
    });
  }, [selfTrainingSelection, exerciseSettings, tasksKey, isSelf]);

  return (
    <View style={styles.container}>
      <SelectLayout
        leftGrid={<SquareGrid items={exerciseTypes} rows={2} columns={2} />}
        rightGrid={<SquareGrid items={buttonsRightSide} rows={2} columns={1} />}
      />
    </View>
  );
}
